import { useApiClient } from '../../api/useApiClient';

/**
 * Servicio de distribución del cliente local de firma (#59, F1B-7, ADR-028 §D3).
 *
 * Consume los endpoints server-side (ROLE_SIGNER):
 *   GET /api/v1/signatures/client/manifest      → versión vigente + por SO {filename, checksum, downloadUrl}
 *   GET /api/v1/signatures/client/download/{os}  → stream del instalador (Bearer requerido)
 */
const MANIFEST_PATH = '/api/v1/signatures/client/manifest';

class SignatureClientService {
  constructor(apiClient) {
    this.apiClient = apiClient;
  }

  /**
   * Manifest del release vigente. Devuelve null si no hay release publicado (404)
   * o si el usuario no tiene permiso (401/403) — el caller decide no mostrar nada.
   * @returns {Promise<{currentVersion, releasedAt, platforms}|null>}
   */
  async getManifest() {
    try {
      const response = await this.apiClient.get(MANIFEST_PATH);
      return response?.data || null;
    } catch (error) {
      const status = error?.response?.status;
      if (status === 404 || status === 401 || status === 403) return null;
      throw error;
    }
  }

  /**
   * Descarga autenticada del instalador (Bearer): el endpoint es SIGNER-protected, así que
   * un <a href> plano no sirve (no manda el token). Bajamos el binario como blob y lo
   * guardamos. `downloadUrl` viene del manifest (p.ej. /api/v1/signatures/client/download/windows).
   */
  async downloadInstaller(downloadUrl, filename) {
    // Por fetch y no por axios: el instalador pesa ~90 MB y por XHR la descarga se cortaba a los
    // ~500 ms, con apenas 10-15 MB bajados y sin código HTTP (fallo de red). El mismo endpoint por
    // fetch entrega los 90 MB completos. Ver downloadBinary en useApiClient.
    const blob = await this.apiClient.downloadBinary(downloadUrl);
    if (!blob || blob.size === 0) throw new Error('Descarga vacía del instalador');

    const objectUrl = window.URL.createObjectURL(blob);
    try {
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filename || 'boveda-firma-installer';
      document.body.appendChild(a);
      a.click();
      a.remove();
    } finally {
      window.URL.revokeObjectURL(objectUrl);
    }
  }
}

export const useSignatureClientService = () => {
  const apiClient = useApiClient();
  return new SignatureClientService(apiClient);
};

export default SignatureClientService;
